import { api } from '@invisoutlet/shared/api';
import { useAuthContext } from '@invisoutlet/shared/context';
import type {
  AlertRule,
  Building,
  Entity,
  RuleCondition,
  SensorKey,
} from '@invisoutlet/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BuildingSearchDropdown } from '../components/building-search-dropdown.js';
import { EnterpriseLayout } from '../components/enterprise-layout.js';

// ─── Constants ────────────────────────────────────────────────────────────────

const SENSOR_LABELS: Record<SensorKey, string> = {
  temperature: 'Temperature',
  humidity: 'Humidity',
  lux: 'Light (Lux)',
  occupancy: 'Occupancy',
  outlet1: 'Outlet 1',
  outlet2: 'Outlet 2',
  deviceStatus: 'Device Status',
};

const OPERATOR_LABELS: Record<string, string> = {
  gt: 'greater than',
  gte: 'greater than or equal to',
  lt: 'less than',
  lte: 'less than or equal to',
  eq: 'equals',
  neq: 'not equals',
};

const NUMERIC_SENSORS: SensorKey[] = ['temperature', 'humidity', 'lux'];
const BOOLEAN_SENSORS: SensorKey[] = ['occupancy', 'outlet1', 'outlet2'];
const NUMERIC_OPERATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'];
const BOOLEAN_OPERATORS = ['eq', 'neq'];
const STATUS_VALUES = ['online', 'offline', 'error'];

const SEVERITY_CONFIG = {
  critical: { dot: 'bg-danger', text: 'text-danger' },
  warning: { dot: 'bg-warning', text: 'text-warning' },
  info: { dot: 'bg-info', text: 'text-info' },
} as const;

function formatCondition(condition: RuleCondition): string {
  const sensor = SENSOR_LABELS[condition.sensor] ?? condition.sensor;
  const op = OPERATOR_LABELS[condition.operator] ?? condition.operator;
  const value = BOOLEAN_SENSORS.includes(condition.sensor)
    ? condition.value
      ? 'On / Occupied'
      : 'Off / Vacant'
    : String(condition.value);
  return `${sensor} ${op} ${value}`;
}

// ─── Rule form state ──────────────────────────────────────────────────────────

interface RuleFormState {
  name: string;
  buildingId: string;
  enabled: boolean;
  sensor: SensorKey;
  operator: string;
  value: string; // always string; coerced on submit
  severity: 'critical' | 'warning' | 'info';
  actions: string[];
  cooldownMinutes: string;
}

const EMPTY_FORM: RuleFormState = {
  name: '',
  buildingId: '',
  enabled: true,
  sensor: 'temperature',
  operator: 'gt',
  value: '',
  severity: 'warning',
  actions: ['banner'],
  cooldownMinutes: '15',
};

function ruleToForm(rule: AlertRule): RuleFormState {
  return {
    name: rule.name,
    buildingId: rule.buildingId,
    enabled: rule.enabled,
    sensor: rule.condition.sensor,
    operator: rule.condition.operator,
    value: String(rule.condition.value),
    severity: rule.severity,
    actions: rule.actions,
    cooldownMinutes: String(rule.cooldownMinutes),
  };
}

// ─── RuleFormPanel ────────────────────────────────────────────────────────────

interface RuleFormPanelProps {
  form: RuleFormState;
  onChange: (patch: Partial<RuleFormState>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  buildings: Building[];
  hasSmsAddon: boolean;
  submitting: boolean;
  errors: Record<string, string>;
}

function RuleFormPanel({
  form,
  onChange,
  onSubmit,
  onCancel,
  buildings,
  hasSmsAddon,
  submitting,
  errors,
}: RuleFormPanelProps) {
  const isNumeric = NUMERIC_SENSORS.includes(form.sensor);
  const isBoolean = BOOLEAN_SENSORS.includes(form.sensor);
  const isStatus = form.sensor === 'deviceStatus';

  const operators = isBoolean || isStatus ? BOOLEAN_OPERATORS : NUMERIC_OPERATORS;

  function handleActionToggle(action: string) {
    onChange({
      actions: form.actions.includes(action)
        ? form.actions.filter((a) => a !== action)
        : [...form.actions, action],
    });
  }

  const inputClass =
    'w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent';
  const errorClass = 'mt-1 text-xs text-danger';
  const labelClass = 'mb-1 block text-sm font-medium text-text-primary';

  return (
    <div className="rounded-xl border border-border bg-bg-primary p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Name */}
        <div className="sm:col-span-2">
          <label htmlFor="rule-name" className={labelClass}>
            Rule name
          </label>
          <input
            id="rule-name"
            type="text"
            value={form.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. High Temperature Alert"
            className={`${inputClass} ${errors.name ? 'border-danger' : ''}`}
          />
          {errors.name && <p className={errorClass}>{errors.name}</p>}
        </div>

        {/* Building */}
        <div>
          <label htmlFor="rule-building" className={labelClass}>
            Building
          </label>
          <select
            id="rule-building"
            value={form.buildingId}
            onChange={(e) => onChange({ buildingId: e.target.value })}
            className={`${inputClass} ${errors.buildingId ? 'border-danger' : ''}`}
          >
            <option value="">Select a building</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {errors.buildingId && <p className={errorClass}>{errors.buildingId}</p>}
        </div>

        {/* Severity */}
        <div>
          <label htmlFor="rule-severity" className={labelClass}>
            Severity
          </label>
          <select
            id="rule-severity"
            value={form.severity}
            onChange={(e) => onChange({ severity: e.target.value as RuleFormState['severity'] })}
            className={inputClass}
          >
            <option value="critical">Critical</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
        </div>

        {/* Sensor */}
        <div>
          <label htmlFor="rule-sensor" className={labelClass}>
            Sensor
          </label>
          <select
            id="rule-sensor"
            value={form.sensor}
            onChange={(e) => {
              const sensor = e.target.value as SensorKey;
              const newOp =
                BOOLEAN_SENSORS.includes(sensor) || sensor === 'deviceStatus' ? 'eq' : 'gt';
              onChange({ sensor, operator: newOp, value: '' });
            }}
            className={inputClass}
          >
            {(Object.entries(SENSOR_LABELS) as [SensorKey, string][]).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Operator */}
        <div>
          <label htmlFor="rule-operator" className={labelClass}>
            Operator
          </label>
          <select
            id="rule-operator"
            value={form.operator}
            onChange={(e) => onChange({ operator: e.target.value })}
            className={inputClass}
          >
            {operators.map((op) => (
              <option key={op} value={op}>
                {OPERATOR_LABELS[op]}
              </option>
            ))}
          </select>
        </div>

        {/* Threshold value */}
        <div className="sm:col-span-2">
          <label htmlFor="rule-threshold" className={labelClass}>
            Threshold value
          </label>
          {isNumeric && (
            <input
              id="rule-threshold"
              type="number"
              value={form.value}
              onChange={(e) => onChange({ value: e.target.value })}
              placeholder={form.sensor === 'temperature' ? 'e.g. 85' : 'Enter value'}
              className={`${inputClass} ${errors.value ? 'border-danger' : ''}`}
            />
          )}
          {isBoolean && (
            <select
              id="rule-threshold"
              value={form.value}
              onChange={(e) => onChange({ value: e.target.value })}
              className={inputClass}
            >
              <option value="true">On / Occupied</option>
              <option value="false">Off / Vacant</option>
            </select>
          )}
          {isStatus && (
            <select
              id="rule-threshold"
              value={form.value}
              onChange={(e) => onChange({ value: e.target.value })}
              className={inputClass}
            >
              <option value="">Select status</option>
              {STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          )}
          {errors.value && <p className={errorClass}>{errors.value}</p>}
        </div>

        {/* Actions */}
        <div className="sm:col-span-2">
          <p className={labelClass}>Alert actions</p>
          <div className="flex flex-wrap gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={form.actions.includes('banner')}
                disabled
                className="rounded border-border"
              />
              <span>Banner (always on)</span>
            </label>
            <label
              className={`flex items-center gap-2 text-sm ${
                hasSmsAddon
                  ? 'cursor-pointer text-text-primary'
                  : 'cursor-not-allowed text-text-secondary'
              }`}
            >
              <input
                type="checkbox"
                checked={form.actions.includes('sms')}
                disabled={!hasSmsAddon}
                onChange={() => handleActionToggle('sms')}
                className="rounded border-border"
              />
              <span>
                SMS
                {!hasSmsAddon && (
                  <span className="ml-1.5 rounded-full bg-bg-elevated px-1.5 py-0.5 text-xs">
                    Add-on required
                  </span>
                )}
              </span>
            </label>
          </div>
        </div>

        {/* Cooldown */}
        <div>
          <label htmlFor="rule-cooldown" className={labelClass}>
            Cooldown (minutes)
          </label>
          <input
            id="rule-cooldown"
            type="number"
            min="1"
            value={form.cooldownMinutes}
            onChange={(e) => onChange({ cooldownMinutes: e.target.value })}
            className={`${inputClass} ${errors.cooldownMinutes ? 'border-danger' : ''}`}
          />
          {errors.cooldownMinutes && <p className={errorClass}>{errors.cooldownMinutes}</p>}
          <p className="mt-1 text-xs text-text-secondary">
            Minimum time between repeat alerts per device.
          </p>
        </div>

        {/* Enable toggle */}
        <div className="flex items-center gap-3 self-end">
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              role="switch"
              aria-checked={form.enabled}
              checked={form.enabled}
              onChange={(e) => onChange({ enabled: e.target.checked })}
              className="sr-only"
              aria-label="Enable this rule"
            />
            <span
              className={`h-5 w-9 rounded-full transition-colors ${
                form.enabled ? 'bg-accent' : 'bg-bg-elevated'
              }`}
            />
            <span
              className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                form.enabled ? 'translate-x-4' : ''
              }`}
            />
          </label>
          <span className="text-sm text-text-primary">{form.enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>

      {/* Form actions */}
      <div className="mt-5 flex justify-end gap-3 border-t border-border pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg px-4 py-2 text-sm text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save rule'}
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RulesPage() {
  const { user } = useAuthContext();
  const entityId = user!.entityId!;
  const queryClient = useQueryClient();

  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Entity
  const { data: entityRes } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;
  const buildings = entity?.buildings ?? [];
  const hasSmsAddon = entity?.addOns?.smsAlerts === true;
  const activeBuildingId = selectedBuildingId || buildings[0]?.id || '';

  // Rules
  const rulesQuery = useQuery({
    queryKey: ['rules', entityId],
    queryFn: () => api.get<AlertRule[]>(`/api/entities/${entityId}/rules`),
    enabled: !!entityId,
  });

  const allRules = rulesQuery.data?.data ?? [];
  const filteredRules = activeBuildingId
    ? allRules.filter((r) => r.buildingId === activeBuildingId)
    : allRules;

  // Mutations
  const createMutation = useMutation({
    mutationFn: (body: unknown) => api.post(`/api/entities/${entityId}/rules`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rules', entityId] });
      closeForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ ruleId, body }: { ruleId: string; body: unknown }) =>
      api.patch(`/api/entities/${entityId}/rules/${ruleId}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rules', entityId] });
      closeForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: string) => api.delete(`/api/entities/${entityId}/rules/${ruleId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rules', entityId] });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ ruleId, enabled }: { ruleId: string; enabled: boolean }) =>
      api.patch(`/api/entities/${entityId}/rules/${ruleId}`, { enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rules', entityId] });
    },
  });

  function openCreate() {
    setEditingRule(null);
    setForm({ ...EMPTY_FORM, buildingId: activeBuildingId });
    setFormErrors({});
    setShowForm(true);
  }

  function openEdit(rule: AlertRule) {
    setEditingRule(rule);
    setForm(ruleToForm(rule));
    setFormErrors({});
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingRule(null);
    setFormErrors({});
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (!form.buildingId) errors.buildingId = 'Select a building.';
    if (NUMERIC_SENSORS.includes(form.sensor)) {
      if (!form.value || Number.isNaN(Number(form.value))) errors.value = 'Enter a valid number.';
    } else if (form.sensor === 'deviceStatus' && !form.value) {
      errors.value = 'Select a device status.';
    }
    const cooldown = parseInt(form.cooldownMinutes, 10);
    if (Number.isNaN(cooldown) || cooldown < 1)
      errors.cooldownMinutes = 'Cooldown must be at least 1 minute.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function buildCondition(): RuleCondition {
    const isBoolean = BOOLEAN_SENSORS.includes(form.sensor);
    const isNumeric = NUMERIC_SENSORS.includes(form.sensor);
    const rawValue = form.value === '' ? undefined : form.value;

    return {
      sensor: form.sensor,
      operator: form.operator as RuleCondition['operator'],
      value: isBoolean ? rawValue === 'true' : isNumeric ? Number(rawValue) : (rawValue ?? ''),
    };
  }

  function handleSubmit() {
    if (!validateForm()) return;

    const body = {
      name: form.name.trim(),
      buildingId: form.buildingId,
      enabled: form.enabled,
      condition: buildCondition(),
      severity: form.severity,
      actions: form.actions,
      cooldownMinutes: parseInt(form.cooldownMinutes, 10),
    };

    if (editingRule) {
      updateMutation.mutate({ ruleId: editingRule.id, body });
    } else {
      createMutation.mutate(body);
    }
  }

  const submitting = createMutation.isPending || updateMutation.isPending;

  return (
    <EnterpriseLayout title="Rules">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-text-primary">Alert Rules</h1>
            <p className="mt-1 text-sm text-text-secondary">
              Configure rules that trigger alerts when sensor thresholds are exceeded.
            </p>
          </div>
          {!showForm && (
            <button
              type="button"
              onClick={openCreate}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              + New rule
            </button>
          )}
        </div>

        {/* Building selector */}
        {buildings.length > 1 && (
          <div className="mb-5">
            <BuildingSearchDropdown
              buildings={buildings}
              selected={selectedBuildingId}
              onChange={setSelectedBuildingId}
              placeholder="Search buildings…"
            />
          </div>
        )}

        {/* Form panel */}
        {showForm && (
          <div className="mb-6">
            <h2 className="mb-3 text-sm font-semibold text-text-primary">
              {editingRule ? 'Edit rule' : 'Create rule'}
            </h2>
            <RuleFormPanel
              form={form}
              onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
              onSubmit={handleSubmit}
              onCancel={closeForm}
              buildings={buildings}
              hasSmsAddon={hasSmsAddon}
              submitting={submitting}
              errors={formErrors}
            />
          </div>
        )}

        {/* Rules list */}
        {rulesQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className="h-16 animate-pulse rounded-xl border border-border bg-bg-primary"
              />
            ))}
          </div>
        ) : filteredRules.length === 0 && !showForm ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-bg-elevated">
              <svg
                className="h-7 w-7 text-text-secondary"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h2 className="mb-2 text-base font-semibold text-text-primary">No rules yet</h2>
            <p className="mb-4 max-w-sm text-sm text-text-secondary">
              Create a rule to get notified when sensor readings exceed a threshold.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              Create your first rule
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredRules.map((rule) => {
              const sev = SEVERITY_CONFIG[rule.severity];
              return (
                <div
                  key={rule.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-border bg-bg-primary px-4 py-3 sm:flex-nowrap"
                >
                  <div className="flex w-full min-w-0 items-center gap-3 sm:w-auto sm:flex-1">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${sev.dot}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text-primary">{rule.name}</p>
                      <p className="text-xs text-text-secondary">
                        {formatCondition(rule.condition)} · cooldown {rule.cooldownMinutes}m
                      </p>
                    </div>
                  </div>
                  <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
                    <span className={`text-xs font-medium capitalize ${sev.text}`}>
                      {rule.severity}
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="relative inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          role="switch"
                          aria-checked={rule.enabled}
                          checked={rule.enabled}
                          onChange={(e) =>
                            toggleMutation.mutate({ ruleId: rule.id, enabled: e.target.checked })
                          }
                          className="sr-only"
                          aria-label={`${rule.enabled ? 'Disable' : 'Enable'} rule "${rule.name}"`}
                        />
                        <span
                          className={`h-5 w-9 rounded-full transition-colors ${
                            rule.enabled ? 'bg-accent' : 'bg-bg-elevated'
                          }`}
                        />
                        <span
                          className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                            rule.enabled ? 'translate-x-4' : ''
                          }`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => openEdit(rule)}
                        className="rounded px-2 py-1 text-xs text-text-secondary ring-1 ring-border hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Delete rule "${rule.name}"?`)) {
                            deleteMutation.mutate(rule.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="rounded px-2 py-1 text-xs text-danger ring-1 ring-danger/30 hover:bg-danger/5 focus:outline-none focus:ring-2 focus:ring-danger disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
}
